import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Componentes e tipos compartilhados (sim-onboarding) ─────────────────────
import {
  SplashStep, SenhaStep, CidadeStep, GestorMunicipalStep, EscolaStep, ResumoStep,
  type Cidade, type Escola,
  calcTotal, SS, criarSessaoSimulacao,
} from '../../sim-onboarding';


type Step = 'splash' | 'senha' | 'cidade' | 'gestor' | 'escola' | 'resumo';

// ─── Página principal ─────────────────────────────────────────────────────────
export function SimOnboardingPage() {
  const navigate = useNavigate();

  // Determina etapa inicial com base na sessão
  function initStep(): Step {
    if (SS.get('sim_auth') !== '1') return 'splash';
    const c = SS.get('sim_cidade');
    if (!c) return 'cidade';
    return 'escola';
  }

  const [step, setStep]       = useState<Step>(initStep);
  const [fadeOut, setFadeOut] = useState(false);

  // Senha
  const [senha, setSenha]       = useState('');
  const [senhaErro, setSenhaErro] = useState(false);
  const [senhaShake, setSenhaShake] = useState(false);

  // Cidade — gerenciada internamente pelo CidadeStep
  const [cidadeSel, setCidadeSel] = useState<Cidade | null>(() => {
    try { const s = SS.get('sim_cidade'); return s ? JSON.parse(s) : null; } catch { return null; }
  });

  // Escolas — gerenciadas internamente pelo EscolaStep
  const [escolasSel, setEscolasSel] = useState<Set<string>>(new Set());

  // Sessão criada no resumo
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Auto-avança splash
  useEffect(() => {
    if (step !== 'splash') return;
    const t = setTimeout(() => goTo('senha'), 3000);
    return () => clearTimeout(t);
  }, [step]);



  function goTo(next: Step) {
    setFadeOut(true);
    setTimeout(() => { setStep(next); setFadeOut(false); }, 340);
  }

  async function handleSenha() {
    try {
      const r = await fetch('/api/v1/auth/sim-studio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: senha.trim() }),
      });
      if (!r.ok) throw new Error('invalid');
      const data = await r.json();
      // Salva token no mesmo local que o login normal usa
      localStorage.setItem('ac_token', data.access_token);
      SS.set('sim_auth', '1');
      goTo('cidade');
    } catch {
      setSenhaErro(true); setSenhaShake(true);
      setTimeout(() => setSenhaShake(false), 600);
    }
  }

  function handleSelectCidade(c: Cidade) {
    setCidadeSel(c);
    SS.set('sim_cidade', JSON.stringify(c));
    // Se não tem gestor, vai para etapa gestor antes de escolher escolas
    goTo(c.temGestor ? 'escola' : 'gestor');
  }

  function handleGestorCriado(c: Cidade) {
    setCidadeSel(c);
    SS.set('sim_cidade', JSON.stringify(c));
  }

  function toggleEscola(e: Escola) {
    setEscolasSel(prev => { const n = new Set(prev); n.has(e.id) ? n.delete(e.id) : n.add(e.id); return n; });
  }

  async function handleLaunch() {
    if (!cidadeSel) return;
    try {
      const session = await criarSessaoSimulacao({
        municipioId: cidadeSel.id,
        escolaIds: Array.from(escolasSel),
      });
      setSessionId(session.id);
      navigate('/sim?sessionId=' + session.id);
    } catch (e: any) {
      console.error('[SimOnboarding] Erro ao criar sessão:', e.message);
      navigate('/sim'); // fallback
    }
  }

  const stepOrder: Step[] = ['senha','cidade','gestor','escola','resumo'];
  const isWide = step === 'escola';

  return (
    <div style={{
      width:'100vw', height:'100vh', overflow:'hidden', position:'relative',
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      background:'linear-gradient(135deg,#060614 0%,#0d0d24 40%,#0a0822 70%,#060614 100%)',
      fontFamily:"'Inter','Segoe UI',system-ui,sans-serif",
    }}>
      {/* Fundo */}
      <div style={{ position:'absolute', inset:0, overflow:'hidden', pointerEvents:'none' }}>
        <div className="orb orb1"/><div className="orb orb2"/><div className="orb orb3"/>
        <div className="grid-overlay"/>
      </div>

      {/* Indicador de progresso */}
      {step !== 'splash' && (
        <div style={{ position:'absolute', top:26, left:'50%', transform:'translateX(-50%)', display:'flex', gap:8, zIndex:10 }}>
          {stepOrder.map((s, i) => {
            const cur = stepOrder.indexOf(step); const done = i < cur; const active = i === cur;
            return <div key={s} style={{
              width: active ? 32 : 8, height:8, borderRadius:4,
              background: done ? '#7c3aed' : active ? '#a78bfa' : 'rgba(255,255,255,0.1)',
              transition:'all 0.4s cubic-bezier(.4,0,.2,1)',
            }}/>;
          })}
        </div>
      )}

      {/* Botão voltar */}
      {(step === 'escola' || step === 'resumo') && (
        <button onClick={() => goTo(step === 'resumo' ? 'escola' : 'cidade')}
          style={{
            position:'absolute', top:22, left:32, zIndex:10,
            background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)',
            borderRadius:10, padding:'7px 14px', cursor:'pointer',
            color:'#94a3b8', fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6,
          }}
          onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.color='#e2e8f0';}}
          onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.06)'; e.currentTarget.style.color='#94a3b8';}}
        >
          ← Voltar
        </button>
      )}

      {/* Conteúdo */}
      <div style={{
        position:'relative', zIndex:10, width:'100%',
        maxWidth: isWide ? 1060 : 560,
        padding: isWide ? '0 32px' : '0 24px',
        opacity: fadeOut ? 0 : 1,
        transform: fadeOut ? 'translateY(14px)' : 'translateY(0)',
        transition:'opacity 0.3s ease, transform 0.3s ease, max-width 0.4s ease',
      }}>
        {step==='splash' && <SplashStep />}
        {step==='senha'  && <SenhaStep senha={senha} setSenha={setSenha} erro={senhaErro} setErro={setSenhaErro} shake={senhaShake} onNext={handleSenha}/>}
        {step==='cidade' && (
          <CidadeStep
            selecionada={cidadeSel}
            onSelect={handleSelectCidade}
            onClear={() => { setCidadeSel(null); SS.del('sim_cidade'); }}
            onNext={() => goTo(cidadeSel?.temGestor ? 'escola' : 'gestor')}
          />
        )}
        {step==='gestor' && cidadeSel && (
          <GestorMunicipalStep
            cidade={cidadeSel}
            onGestorCriado={handleGestorCriado}
            onNext={() => goTo('escola')}
          />
        )}
        {step==='escola' && cidadeSel && (
          <EscolaStep
            cidade={cidadeSel}
            selecionados={escolasSel}
            onToggle={toggleEscola}
            onNext={() => goTo('resumo')}
          />
        )}
        {step==='resumo' && cidadeSel && (
          <ResumoStep
            escolas={Array.from(escolasSel) as any}
            cidade={cidadeSel!}
            resumo={calcTotal([] as Escola[])}
            onLaunch={handleLaunch}
          />
        )}
      </div>
      <Styles/>
    </div>
  );
}



function Styles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
      .orb{position:absolute;border-radius:50%;filter:blur(80px);animation:orbFloat 8s ease-in-out infinite}
      .orb1{width:500px;height:500px;background:rgba(124,58,237,.18);top:-150px;left:-100px;animation-delay:0s}
      .orb2{width:400px;height:400px;background:rgba(79,70,229,.12);bottom:-100px;right:-80px;animation-delay:-3s}
      .orb3{width:300px;height:300px;background:rgba(167,139,250,.08);top:40%;left:60%;animation-delay:-6s}
      @keyframes orbFloat{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(30px,-20px) scale(1.05)}66%{transform:translate(-20px,30px) scale(.95)}}
      .grid-overlay{position:absolute;inset:0;background-image:linear-gradient(rgba(124,58,237,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(124,58,237,.04) 1px,transparent 1px);background-size:48px 48px;mask-image:radial-gradient(ellipse at center,black 30%,transparent 80%)}
      .splash-logo{position:relative;width:120px;height:120px;display:flex;align-items:center;justify-content:center;animation:splashIn .8s cubic-bezier(.34,1.56,.64,1) both}
      .splash-ring{position:absolute;border-radius:50%;border:1px solid rgba(124,58,237,.25);animation:ringPulse 2.5s ease-out infinite}
      .splash-ring1{width:120px;height:120px;animation-delay:0s}.splash-ring2{width:160px;height:160px;animation-delay:.4s}.splash-ring3{width:200px;height:200px;animation-delay:.8s}
      @keyframes ringPulse{0%{opacity:.6;transform:scale(.9)}100%{opacity:0;transform:scale(1.2)}}
      @keyframes splashIn{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:scale(1)}}
      .splash-text{animation:fadeUp .6s .5s ease both}
      .splash-loader{width:160px;height:3px;background:rgba(255,255,255,.08);border-radius:2px;overflow:hidden;animation:fadeUp .4s .8s ease both}
      .splash-loader-bar{height:100%;background:linear-gradient(90deg,#7c3aed,#a78bfa);animation:loadBar 2.5s 1s ease-out both}
      @keyframes loadBar{from{width:0}to{width:100%}}
      @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
      .shake{animation:shake .5s cubic-bezier(.36,.07,.19,.97) both}
      @keyframes shake{10%,90%{transform:translateX(-2px)}20%,80%{transform:translateX(4px)}30%,50%,70%{transform:translateX(-6px)}40%,60%{transform:translateX(6px)}}
      input[type=range]{-webkit-appearance:none;height:4px;border-radius:2px;background:rgba(74,222,128,.2);outline:none}
      input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;border-radius:50%;background:#4ade80;cursor:pointer;box-shadow:0 0 6px rgba(74,222,128,.5)}
    `}</style>
  );
}
